(function () {
  'use strict';

  /* Shared mouse drag-to-scroll for the eb- horizontal sliders. Touch and
     trackpad keep native scrolling; this only adds click-and-drag panning for
     mouse pointers, and the track's CSS scroll-snap settles the row on release.

     Three traps it centralises — each was a live bug in the per-section copies
     this replaced:

     - The drag baseline (start x + scrollLeft) has to be read when the drag
       passes the threshold, NOT on pointerdown. The settle animation from the
       previous release is still running in between, so a pointerdown baseline
       is applied several frames later against a position that has moved on:
       the row slid backwards under the cursor, then jumped ~40px forward the
       instant the threshold was crossed. That is the "drag it a few times in a
       row and it goes crazy" bug.
     - That pending animation also has to be stopped on pointerdown, or the row
       keeps sliding while the press is held.
     - pointerup can simply never arrive — a right-click, a native image/link
       drag, or a release outside the window. The row was then left panning with
       no button held and its cards stuck at pointer-events: none.

     Options: `dragClass` (default `is-dragging`), `onStart`/`onEnd` callbacks,
     and `keepClick(event)` to let a control keep its click after a drag. */

  var THRESHOLD = 8;

  function attach(track, options) {
    if (!track || track.ebDragScrollAttached) return;
    track.ebDragScrollAttached = true;

    var opts = options || {};
    var dragClass = opts.dragClass || 'is-dragging';

    var pointerId = null;
    var dragging = false;
    var swallowClick = false;
    var startX = 0;
    var startScroll = 0;

    function endDrag() {
      if (pointerId !== null && track.hasPointerCapture(pointerId)) {
        track.releasePointerCapture(pointerId);
      }
      pointerId = null;
      track.style.scrollBehavior = '';
      track.style.scrollSnapType = '';
      if (!dragging) return;
      dragging = false;
      track.classList.remove(dragClass);
      if (opts.onEnd) opts.onEnd();
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || pointerId !== null) return;
      pointerId = e.pointerId;
      swallowClick = false;
      startX = e.clientX;
      /* Freeze the row before anything reads its position: a settle or arrow
         animation may still be in flight, and an `instant` scroll aborts it
         where it stands. */
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });
    });

    track.addEventListener('pointermove', function (e) {
      if (e.pointerId !== pointerId) return;
      if (!e.buttons) {
        endDrag();
        return;
      }

      if (!dragging) {
        if (Math.abs(e.clientX - startX) <= THRESHOLD) return;
        /* Baseline taken here, at the real start of the drag. Capture waits for
           this moment too: a captured pointer retargets the following click to
           the track, which would swallow a plain click on a card link. */
        dragging = true;
        swallowClick = true;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        try {
          track.setPointerCapture(pointerId);
        } catch (err) {
          /* The pointer can already be gone; panning still works without it. */
        }
        track.classList.add(dragClass);
        if (opts.onStart) opts.onStart();
        return;
      }

      track.scrollLeft = startScroll - (e.clientX - startX);
      e.preventDefault();
    });

    track.addEventListener('pointerup', function (e) {
      if (e.pointerId === pointerId) endDrag();
    });

    track.addEventListener('pointercancel', function (e) {
      if (e.pointerId === pointerId) endDrag();
    });

    /* Native image/link dragging and text selection both steal the gesture
       mid-drag, which is how pointerup used to go missing. */
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    track.addEventListener('selectstart', function (e) {
      if (pointerId !== null) e.preventDefault();
    });

    /* Swallow the click that follows a real drag so links don't fire. */
    track.addEventListener(
      'click',
      function (e) {
        if (!swallowClick) return;
        swallowClick = false;
        if (opts.keepClick && opts.keepClick(e)) return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }

  window.EbDragScroll = { attach: attach };
})();
